import React, { memo, Suspense, lazy, useEffect } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import HeroSection from '../components/landing/HeroSection';
const StatsSection = lazy(() => import('../components/landing/StatsSection'));
const IssueDetectionSection = lazy(() => import('../components/landing/IssueDetectionSection'));
const FeaturesSection = lazy(() => import('../components/landing/FeaturesSection'));
const InteractiveWorkflow = lazy(() => import('../components/landing/InteractiveWorkflow'));
const AICoreBenefits = lazy(() => import('../components/landing/AICoreBenefits'));
const PricingSection = lazy(() => import('../components/landing/PricingSection'));
const Footer = lazy(() => import('../components/landing/Footer'));
const SniffrAdapts = lazy(() => import('../components/landing/SniffrAdapts'));
const FAQSection = lazy(() => import('../components/landing/FAQSection'));

const HOME_FAQS = [
  {
    question: "What is Sniffr.ai and how does it work?",
    answer: "Sniffr.ai is an AI-powered code review and UAT automation engine. It connects to your Git repository (GitHub, GitLab, Bitbucket, or Azure DevOps) and automatically reviews every pull request — checking code quality, security vulnerabilities, requirement alignment, and acceptance criteria. It uses large language models to simulate the work of a senior tech lead, QA engineer, and compliance reviewer all in one.",
  },
  {
    question: "How does Sniffr.ai catch 80% of defects before QA?",
    answer: "Sniffr.ai analyzes pull requests against your JIRA requirements, acceptance criteria, and architectural patterns. It detects logic errors, edge cases, missing validations, security flaws, and requirement mismatches that human reviewers typically miss. By catching these issues at the PR stage — before code is merged — the defect rate reaching QA drops significantly.",
  },
  {
    question: "Which platforms does Sniffr.ai integrate with?",
    answer: "Sniffr.ai integrates with GitHub, GitLab, Bitbucket, and Azure DevOps for code repositories. For project management, it supports JIRA, Azure Boards, and Trello. For AI analysis, it leverages OpenAI (GPT-4), Google Gemini, and Anthropic Claude. Setup takes less than 10 minutes via a simple webhook integration.",
  },
  {
    question: "How quickly can I get Sniffr.ai set up?",
    answer: "Most teams are up and running within 10 minutes. You create a project, connect your Git repository via webhook, configure your AI analyzer preferences, and Sniffr.ai immediately starts reviewing your next pull request. No code changes or CI/CD pipeline modifications are needed.",
  },
  {
    question: "Is Sniffr.ai suitable for my team size?",
    answer: "Sniffr.ai works for teams of all sizes — from solo developers to enterprise engineering organizations with hundreds of contributors. It offers flexible pricing plans including a free tier, growth plans for scaling teams, and enterprise options with custom SLAs, dedicated support, and SSO.",
  },
  {
    question: "How does Sniffr.ai handle data security and privacy?",
    answer: "Sniffr.ai is designed with security-first principles. Code analysis happens in isolated environments, data is encrypted in transit and at rest, and we do not store your source code beyond the analysis window. We maintain compliance with SOC 2 standards and provide detailed audit logs for all AI analysis activity.",
  },
];

const ROOT_SX = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'MD IO, monospace',
  bgcolor: '#fff'
};

const CONTENT_SX = {
  maxWidth: '100vw',
  overflowX: 'hidden',
};

const MAIN_SX = {
  flex: 1,
  fontFamily: 'MD IO, monospace'
};

const LandingPage = () => {
  const location = useLocation();

  // When navigating here from another page with a hash target (e.g. #features, #pricing)
  useEffect(() => {
    const hash = location.state?.scrollTo;
    if (!hash) return;

    // Use requestAnimationFrame to wait for the page to paint before scrolling
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [location.state]);

  return (
    <>
      <SEO
        title="Sniffr.ai | AI Code Review & Requirement Validation for Engineering Teams"
        description="Catch defects before QA. Sniffr.ai reviews pull requests, requirements, and acceptance criteria with AI. Book Sniffr Demo today."
        keywords="UAT automation, AI UAT automation, requirement validation, AI code review, automated code review, pull request analysis, code quality, GitHub review, GitLab review, Bitbucket review, Azure DevOps review, code analysis tool, software development, DevOps, AI-powered code analysis, security code review, JIRA validation, acceptance criteria validation, SDLC validation, engineering accuracy scoring"
        url="/"
      />
      <Box sx={ROOT_SX}>
        <Box sx={CONTENT_SX}>
          <Box component="main" sx={MAIN_SX}>
            <HeroSection />
            <Suspense fallback={null}>
              <FeaturesSection />
            </Suspense>
            <Suspense fallback={null}>
              <StatsSection />
            </Suspense>
            <Suspense fallback={null}>
              <IssueDetectionSection />
            </Suspense>
            <Suspense fallback={null}>
              <InteractiveWorkflow />
            </Suspense>
            <Suspense fallback={null}>
              <AICoreBenefits />
            </Suspense>
            <Suspense fallback={null}>
              <PricingSection />
            </Suspense>
            <Suspense fallback={null}>
              <SniffrAdapts />
            </Suspense>
            <Suspense fallback={null}>
              <FAQSection faqs={HOME_FAQS} title="Frequently Asked Questions" />
            </Suspense>
          </Box>
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

export default LandingPage;